import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckIcon, ShoppingBagIcon } from 'lucide-react';
import { Product } from '../types';
import { formatPrice, stockLabel } from '../utils/format';
import { useCart } from '../contexts/CartContext';
interface ProductCardProps {
  product: Product;
  view?: 'grid' | 'list';
}
export function ProductCard({
  product,
  view = 'grid'
}: ProductCardProps) {
  const {
    addItem
  } = useCart();
  const navigate = useNavigate();
  const [added, setAdded] = React.useState(false);
  const stock = stockLabel(product.stock);
  const soldOut = product.stock === 0;
  const price = product.salePrice ?? product.price;
  const handleAdd = () => {
    if (soldOut) return;
    addItem(product.id, product.colors[0]?.id ?? 'black');
    setAdded(true);
    navigate('/carrito');
  };
  const badges = <div className="absolute left-3 top-3 flex flex-col items-start gap-1.5" data-id="element-673" data-mp="2v67kmarw41z4">
      {product.salePrice && <span className="bg-pink-500 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white" data-id="element-674" data-mp="303zn1yq5v6hk">
          Oferta
        </span>}
      {product.isNew && <span className="bg-teal-500 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-black" data-id="element-675" data-mp="3gzc6dntig5jt">
          Nuevo
        </span>}
    </div>;
  const addButton = <button type="button" onClick={handleAdd} disabled={soldOut} className={`flex w-full items-center justify-center gap-2 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.16em] transition-colors ${soldOut ? 'cursor-not-allowed bg-ink-600 text-white/35' : added ? 'bg-teal-500 text-black' : 'bg-pink-500 text-white hover:bg-pink-600'}`} data-id="element-676" data-mp="3808noky99s5u">
      {soldOut ? 'Sin stock' : added ? <>
          <CheckIcon className="h-4 w-4" data-id="element-677" data-mp="2kawdbhr6ze1y" /> Agregado
        </> : <>
          <ShoppingBagIcon className="h-4 w-4" data-id="element-678" data-mp="2faigkvv1jqj8" /> Agregar al carrito
        </>}
    </button>;
  if (view === 'list') {
    return <article className="group flex flex-col gap-5 border border-white/10 bg-ink-800 p-4 transition-all duration-300 hover:border-pink-500/60 hover:shadow-pink sm:flex-row" data-id="element-679" data-mp="2gsqim7ozz5cj">
        <Link to={`/producto/${product.slug}`} className="relative block w-full shrink-0 overflow-hidden bg-black sm:w-48" data-id="element-680" data-mp="27qha1ndahm1s">
          <img src={product.images[0]} alt={product.name} className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:h-full" data-id="element-681" data-mp="1y1ftnw2u12na" />
          {badges}
        </Link>
        <div className="flex flex-1 flex-col" data-id="element-682" data-mp="1p22kz5mj8i3m">
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-pink-400" data-id="element-683" data-mp="1t2cq6g2i34hf">
            {product.categoryLabel}
          </span>
          <h3 className="mt-2 font-display text-2xl uppercase leading-none tracking-wide text-white" data-id="element-684" data-mp="1f3wkmsgdjk0v">
            <Link to={`/producto/${product.slug}`} className="hover:text-pink-400" data-id="element-685" data-mp="1k0d9jovee2f0">
              {product.name}
            </Link>
          </h3>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/50" data-id="element-686" data-mp="vgtdh9ofnywz">
            {product.shortDescription}
          </p>
          <div className="mt-3 flex items-center gap-2 text-[11px]" data-id="element-687" data-mp="1aecbl2n03oh3">
            <span className={`h-2 w-2 rounded-full ${stock.dot}`} aria-hidden data-id="element-688" data-mp="15ezfdra8p51u" />
            <span className={stock.text} data-id="element-689" data-mp="15h2fx590qgkt">{stock.label}</span>
          </div>
          <div className="mt-auto flex flex-wrap items-end justify-between gap-4 pt-5" data-id="element-690" data-mp="1jd63ifpdzqob">
            <div className="flex items-baseline gap-2" data-id="element-691" data-mp="1eew7k2vu8m1n">
              <span className="font-display text-3xl leading-none text-white" data-id="element-692" data-mp="1t4aivgiz5djo">
                {formatPrice(price)}
              </span>
              {product.salePrice && <span className="text-sm text-white/35 line-through" data-id="element-693" data-mp="1o7k0unnm07vm">
                  {formatPrice(product.price)}
                </span>}
            </div>
            <div className="w-full sm:w-56" data-id="element-694" data-mp="1y57zvps901jl">{addButton}</div>
          </div>
        </div>
      </article>;
  }
  return <article className="group flex h-full flex-col border border-white/10 bg-ink-800 transition-all duration-300 hover:-translate-y-1.5 hover:border-pink-500/60 hover:shadow-pink" data-id="element-695" data-mp="3rk9lqdmsw4ou">
      <Link to={`/producto/${product.slug}`} className="relative block overflow-hidden bg-black" data-id="element-696" data-mp="57b0q38em2en">
        <img src={product.images[0]} alt={product.name} className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-697" data-mp="a7p1r1bl8c36" />
        {badges}
      </Link>
      <div className="flex flex-1 flex-col p-5" data-id="element-698" data-mp="3il62n9nmvpac">
        <div className="flex items-center justify-between gap-2" data-id="element-699" data-mp="3ike5huee1jhy">
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-pink-400" data-id="element-700" data-mp="3mnj4vo36952d">
            {product.categoryLabel}
          </span>
          <span className="flex items-center gap-1.5 text-[11px]" data-id="element-701" data-mp="xxjg5nbk28pj">
            <span className={`h-2 w-2 rounded-full ${stock.dot}`} aria-hidden data-id="element-702" data-mp="11vqwwi60ra0q" />
            <span className={stock.text} data-id="element-703" data-mp="2mfcj28d4y3sh">{stock.label}</span>
          </span>
        </div>
        <h3 className="mt-2.5 font-display text-2xl uppercase leading-none tracking-wide text-white" data-id="element-704" data-mp="2id92547p66zt">
          <Link to={`/producto/${product.slug}`} className="hover:text-pink-400" data-id="element-705" data-mp="2dlzcw4q295og">
            {product.name}
          </Link>
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-white/45" data-id="element-706" data-mp="28nfrc5fdpvwm">
          {product.shortDescription}
        </p>
        <div className="mt-4 flex items-baseline gap-2" data-id="element-707" data-mp="23m8is45ewsow">
          <span className="font-display text-3xl leading-none text-white" data-id="element-708" data-mp="1yogvjnfppur1">
            {formatPrice(price)}
          </span>
          {product.salePrice && <span className="text-sm text-white/35 line-through" data-id="element-709" data-mp="1pp3g8e4wqbye">
              {formatPrice(product.price)}
            </span>}
        </div>
        <div className="mt-5 flex-1" data-id="element-710" data-mp="3p1fppbyktdhr" />
        {addButton}
      </div>
    </article>;
}